import { Client, type Room } from "colyseus.js";
import type { JoinOptions } from "@scb/protocol";
import type { BattleView, GameSession } from "@scb/client-core";

/**
 * 접속할 Colyseus 엔드포인트.
 *
 * `VITE_SERVER_URL`이 있으면 그걸 쓴다 (로컬에서 클라이언트만 5173으로 띄우고
 * 서버는 2567에 있는 경우). 없으면 **지금 페이지가 서비스되는 곳**을 그대로 쓴다 —
 * 서버가 클라이언트를 함께 서빙하는 배포에서는 언제나 같은 오리진이기 때문이다.
 *
 * 경로까지 포함하는 게 핵심이다. game.stonkradar.io 허브 뒤에서는 문서가
 * `/{slug}/`에 있고 허브가 그 앞부분을 떼어 오리진으로 넘긴다. 엔드포인트에
 * 슬러그를 안 붙이면 matchmaking 요청이 허브 루트로 가서 게임을 못 찾는다.
 */
function serverUrl(): string {
  const configured = import.meta.env.VITE_SERVER_URL;
  if (configured) return configured;

  const protocol = location.protocol === "https:" ? "wss:" : "ws:";
  // 문서 경로에서 파일 이름만 떼어낸 것이 곧 base다.
  //   /            → ''          (단독 도메인)
  //   /scb/        → '/scb'      (허브 뒤)
  //   /scb/index.html → '/scb'
  const base = location.pathname.replace(/\/[^/]*$/, "");
  return `${protocol}//${location.host}${base}`;
}

const SERVER_URL = serverUrl();

/** 온라인 방에 들어가는 방법. */
export type JoinMethod =
  /** 자동 매칭 — 같은 설정의 빈 방에 들어가거나 새로 만든다 */
  | { kind: "quick" }
  /** 방 코드로 입장 (초대 링크 ?room=코드) */
  | { kind: "code"; code: string };

/** 방 코드(roomId)까지 노출하는 온라인 세션. */
export interface RemoteSession extends GameSession {
  roomId: string;
}

/**
 * Colyseus 룸을 GameSession으로 감싼다.
 *
 * Colyseus는 핸드셰이크 때 스키마 정의를 리플렉션으로 내려주므로 클라이언트에
 * 서버 Schema 클래스를 복제해둘 필요가 없다. 대신 BattleView로 느슨하게 타이핑한다.
 */
export async function createRemoteSession(
  options: JoinOptions,
  method: JoinMethod = { kind: "quick" },
): Promise<RemoteSession> {
  const client = new Client(SERVER_URL);
  const room: Room<BattleView> =
    method.kind === "code"
      ? await client.joinById<BattleView>(method.code.trim().toUpperCase(), options)
      : await client.joinOrCreate<BattleView>("battle", options);

  return {
    kind: "online",
    sessionId: room.sessionId,
    roomId: room.roomId,
    get state() {
      return room.state;
    },
    send(type, payload) {
      room.send(type, payload);
    },
    onStateChange(cb) {
      room.onStateChange(cb);
    },
    onMessage(type, cb) {
      room.onMessage(type, cb);
    },
    async leave() {
      // Room.leave()는 종료 코드를 돌려주지만 UI에서는 쓸 일이 없다.
      await room.leave();
    },
  };
}
